import React, { useState, useEffect, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import StorageIcon from '@material-ui/icons/Storage';
import LockIcon from '@material-ui/icons/Lock';

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 500,
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: theme.palette.background.default,
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  sqlEditor: {
    fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontSize: 13,
  },
  sqlToolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  resultPaper: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontSize: 12,
    maxHeight: 400,
    overflow: 'auto',
    whiteSpace: 'pre-wrap' as const,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  prdBadge: {
    backgroundColor: '#d32f2f',
    color: '#fff',
  },
  devBadge: {
    backgroundColor: '#388e3c',
    color: '#fff',
  },
  objectCount: {
    marginLeft: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
}));

/* ─── Types ─── */

interface DbObjects {
  tables: { data: string | null; error: string | null };
  views: { data: string | null; error: string | null };
  indexes: { data: string | null; error: string | null };
  triggers: { data: string | null; error: string | null };
  sequences?: { data: string | null; error: string | null };
}

interface ObjectsResponse {
  dbName: string;
  namespace: string;
  dbType: string;
  database: string;
  isPrd: boolean;
  objects: DbObjects;
}

/* ─── Helpers ─── */

function parseTsv(tsv: string): { headers: string[]; rows: string[][] } {
  if (!tsv || !tsv.trim()) return { headers: [], rows: [] };
  const lines = tsv.trim().split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = lines[0].split('\t');
  const rows = lines.slice(1).map(line => line.split('\t'));
  return { headers, rows };
}

function parseCsv(csv: string): { headers: string[]; rows: string[][] } {
  if (!csv || !csv.trim()) return { headers: [], rows: [] };
  const lines = csv.trim().split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };

  // Simple CSV parse (no quoted fields with commas)
  const headers = lines[0].split(',');
  const rows = lines.slice(1).map(line => line.split(','));
  return { headers, rows };
}

/* ─── Data Table ─── */

function DataTable({
  data,
  dbType,
  emptyMessage,
}: {
  data: string | null;
  dbType: string;
  emptyMessage?: string;
}) {
  const classes = useStyles();

  if (!data || !data.trim()) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyMessage || 'No data'}
      </Typography>
    );
  }

  const parsed = dbType === 'postgres' ? parseCsv(data) : parseTsv(data);
  if (parsed.rows.length === 0 && parsed.headers.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyMessage || 'No data'}
      </Typography>
    );
  }

  return (
    <TableContainer className={classes.tableContainer}>
      <Table size="small" stickyHeader>
        {parsed.headers.length > 0 && (
          <TableHead>
            <TableRow>
              {parsed.headers.map((h, i) => (
                <TableCell key={i} className={classes.headerCell}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {(parsed.headers.length > 0 ? parsed.rows : [parsed.headers, ...parsed.rows]).map(
            (row, ri) => (
              <TableRow key={ri}>
                {row.map((cell, ci) => (
                  <TableCell key={ci}>{cell}</TableCell>
                ))}
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ─── Table Detail (Columns) ─── */

function TableDetail({
  tableName,
  dbName,
  namespace,
  dbType,
  fetchApi,
}: {
  tableName: string;
  dbName: string;
  namespace: string;
  dbType: string;
  fetchApi: any;
}) {
  // hook 호출 순서 보존을 위해 호출만 유지 (반환값 미사용)
  useStyles();
  const [data, setData] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchApi
      .fetch('/api/db-tool/columns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbName, namespace, dbType, tableName }),
      })
      .then((r: Response) => r.json())
      .then((d: any) => {
        if (!cancelled) {
          setData(d.data);
          setError(d.error || null);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tableName, dbName, namespace, dbType, fetchApi]);

  if (loading) return <CircularProgress size={16} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box pl={4} pr={2} pb={1}>
      <DataTable data={data} dbType={dbType} emptyMessage="No columns found" />
    </Box>
  );
}

/* ─── Tables Panel (with expandable rows) ─── */

function TablesPanel({
  data,
  dbType,
  dbName,
  namespace,
  fetchApi,
}: {
  data: string | null;
  dbType: string;
  dbName: string;
  namespace: string;
  fetchApi: any;
}) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!data || !data.trim()) {
    return (
      <Typography variant="body2" color="textSecondary">
        No tables found
      </Typography>
    );
  }

  const parsed = dbType === 'postgres' ? parseCsv(data) : parseTsv(data);

  return (
    <TableContainer className={classes.tableContainer}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell} style={{ width: 40 }} />
            {parsed.headers.map((h, i) => (
              <TableCell key={i} className={classes.headerCell}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {parsed.rows.map((row, ri) => {
            const tblName = row[0];
            const isExpanded = expanded === tblName;
            return (
              <React.Fragment key={ri}>
                <TableRow
                  className={classes.clickableRow}
                  onClick={() => setExpanded(isExpanded ? null : tblName)}
                >
                  <TableCell>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </TableCell>
                  {row.map((cell, ci) => (
                    <TableCell key={ci}>{cell}</TableCell>
                  ))}
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={parsed.headers.length + 1} style={{ padding: 0 }}>
                      <Collapse in={isExpanded}>
                        <TableDetail
                          tableName={tblName}
                          dbName={dbName}
                          namespace={namespace}
                          dbType={dbType}
                          fetchApi={fetchApi}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ─── SQL Editor (P2) ─── */

function SqlEditor({
  dbName,
  namespace,
  dbType,
  isPrd,
  fetchApi,
}: {
  dbName: string;
  namespace: string;
  dbType: string;
  isPrd: boolean;
  fetchApi: any;
}) {
  const classes = useStyles();
  const [sql, setSql] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExecute = useCallback(async () => {
    if (!sql.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const resp = await fetchApi.fetch('/api/db-tool/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbName, namespace, dbType, sql }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || `HTTP ${resp.status}`);
      } else {
        setResult(data);
        if (data.stderr && data.stderr.trim()) {
          setError(data.stderr);
        }
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [sql, dbName, namespace, dbType, fetchApi]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleExecute();
    }
  };

  return (
    <Box>
      {isPrd && (
        <Alert severity="warning" style={{ marginBottom: 8 }}>
          <strong>Prd 환경</strong> — SELECT / SHOW / DESCRIBE만 허용됩니다.
        </Alert>
      )}
      <Box className={classes.sqlToolbar}>
        <TextField
          multiline
          minRows={3}
          maxRows={12}
          fullWidth
          variant="outlined"
          placeholder={
            isPrd
              ? 'SELECT * FROM table_name LIMIT 10;'
              : 'SELECT, DDL (CREATE/ALTER/DROP TABLE), DML...'
          }
          value={sql}
          onChange={e => setSql(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            className: classes.sqlEditor,
          }}
        />
        <Box display="flex" flexDirection="column" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={handleExecute}
            disabled={loading || !sql.trim()}
          >
            Run
          </Button>
          <Typography variant="caption" color="textSecondary">
            Ctrl+Enter
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Box mt={1}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Chip
              size="small"
              label={result.sqlType?.toUpperCase()}
              color={result.sqlType === 'read' ? 'default' : 'secondary'}
            />
            {result.exitCode === 0 ? (
              <Chip size="small" label="OK" style={{ backgroundColor: '#388e3c', color: '#fff' }} />
            ) : (
              <Chip size="small" label={`Exit ${result.exitCode}`} color="secondary" />
            )}
          </Box>
          {result.data ? (
            <Paper className={classes.resultPaper}>{result.data}</Paper>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Query executed successfully (no output)
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ─── Main Component ─── */

export const DbToolContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);

  const [tab, setTab] = useState(0);
  const [objectsData, setObjectsData] = useState<ObjectsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dbName = entity?.metadata?.name || '';
  const namespace =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ||
    entity?.metadata?.namespace ||
    '';
  const dbType =
    entity?.metadata?.annotations?.['resource-type'] || 'mysql';

  const loadObjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchApi.fetch('/api/db-tool/objects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbName, namespace, dbType }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || `HTTP ${resp.status}`);
      } else {
        setObjectsData(data);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dbName, namespace, dbType, fetchApi]);

  useEffect(() => {
    if (dbName && namespace) {
      loadObjects();
    }
  }, [dbName, namespace, loadObjects]);

  if (!dbName || !namespace) {
    return (
      <Alert severity="warning">
        DB name or namespace annotation not found on this entity.
      </Alert>
    );
  }

  const isPrd = objectsData?.isPrd ?? !namespace.endsWith('-dev');

  const countLines = (data: string | null) => {
    if (!data || !data.trim()) return 0;
    const lines = data.trim().split('\n');
    // Subtract header line for TSV (mysql) or CSV (postgres)
    return Math.max(0, lines.length - 1);
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <StorageIcon />
        <Typography variant="h6">{dbName}</Typography>
        <Chip
          size="small"
          label={dbType.toUpperCase()}
          variant="outlined"
        />
        <Chip
          size="small"
          label={isPrd ? 'Prd' : 'Dev'}
          className={isPrd ? classes.prdBadge : classes.devBadge}
        />
        {isPrd && (
          <Tooltip title="Prd: read-only">
            <LockIcon fontSize="small" color="error" />
          </Tooltip>
        )}
        {objectsData?.database && (
          <Chip size="small" label={`DB: ${objectsData.database}`} variant="outlined" />
        )}
        <Box flexGrow={1} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadObjects} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {loading && !objectsData ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Tabs */}
          <Paper>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    Tables
                    <Chip
                      size="small"
                      label={countLines(objectsData?.objects?.tables?.data ?? null)}
                      className={classes.objectCount}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    Views
                    <Chip
                      size="small"
                      label={countLines(objectsData?.objects?.views?.data ?? null)}
                      className={classes.objectCount}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    Indexes
                    <Chip
                      size="small"
                      label={countLines(objectsData?.objects?.indexes?.data ?? null)}
                      className={classes.objectCount}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    Triggers
                    <Chip
                      size="small"
                      label={countLines(objectsData?.objects?.triggers?.data ?? null)}
                      className={classes.objectCount}
                    />
                  </Box>
                }
              />
              <Tab label="SQL Editor" />
            </Tabs>
          </Paper>

          <Box className={classes.tabPanel}>
            {tab === 0 && objectsData && (
              <TablesPanel
                data={objectsData.objects.tables?.data}
                dbType={dbType}
                dbName={dbName}
                namespace={namespace}
                fetchApi={fetchApi}
              />
            )}
            {tab === 1 && objectsData && (
              <DataTable
                data={objectsData.objects.views?.data}
                dbType={dbType}
                emptyMessage="No views found"
              />
            )}
            {tab === 2 && objectsData && (
              <DataTable
                data={objectsData.objects.indexes?.data}
                dbType={dbType}
                emptyMessage="No indexes found"
              />
            )}
            {tab === 3 && objectsData && (
              <DataTable
                data={objectsData.objects.triggers?.data}
                dbType={dbType}
                emptyMessage="No triggers found"
              />
            )}
            {tab === 4 && (
              <SqlEditor
                dbName={dbName}
                namespace={namespace}
                dbType={dbType}
                isPrd={isPrd}
                fetchApi={fetchApi}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
